
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
// import { supabase } from "@/integrations/supabase/client"; // Temporarily commented out
import { Part, parts as staticParts } from "@/data/parts";
import PartCard from "@/components/PartCard";
import { BookmarkX } from "lucide-react";
import { Header } from "@/components/Header";

const SavedItems = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [savedParts, setSavedParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(false); // Set to false to skip loading state

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }

    if (user) {
      // Instead of fetching from Supabase, use local static data
      fetchStaticSavedItems();
    }
  }, [user, isLoading, navigate]);

  // Use static data instead of Supabase
  const fetchStaticSavedItems = () => {
    // Use a few random parts from the static data as "saved" items
    const mockSavedItems = staticParts.slice(0, 3);
    setSavedParts(mockSavedItems);
    setLoading(false);
  };

  // Original fetchSavedItems function commented out
  /*
  const fetchSavedItems = async () => {
    try {
      setLoading(true);
      
      // First get the IDs of saved items
      const { data: savedItems, error: savedItemsError } = await supabase
        .from('saved_items')
        .select('part_id')
        .eq('user_id', user!.id);
        
      if (savedItemsError) throw savedItemsError;

      if (savedItems && savedItems.length > 0) {
        const partIds = savedItems.map(item => parseInt(item.part_id));
        
        // Then fetch the actual parts
        const { data: parts, error: partsError } = await supabase
          .from('parts')
          .select('*')
          .in('id', partIds);
          
        if (partsError) throw partsError;
        
        // Map database fields to Part interface with explicit type assertion
        const mappedParts = (parts?.map(part => ({
          id: part.id,
          name: part.name,
          description: part.description,
          category: part.category,
          subcategory: part.subcategory as Part['subcategory'], // Type assertion to handle extended subcategory values
          filters: part.filters,
          rating: part.rating,
          requiresRoot: part.requires_root || false,
          requiresWiring: part.requires_wiring || false,
          requiresPrinting: part.requires_printing || false,
          requiresTools: part.requires_tools || false,
          requiresPurchase: part.requires_purchase || false,
          amazonLink: part.amazon_link || '',
          aliExpressLink: part.ali_express_link || '',
          videoGuide: part.video_guide || '',
          writtenGuide: part.written_guide || '',
          printedParts: part.printed_parts || '',
          imageUrl: part.image_url || '',
          manufacturer: part.manufacturer || ''
        })) || []) as Part[];
        
        setSavedParts(mappedParts);
      } else {
        setSavedParts([]);
      }
    } catch (error) {
      console.error("Error fetching saved items:", error);
      toast.error("Failed to load saved items");
    } finally {
      setLoading(false);
    }
  };
  */

  const handleRemoveFromSaved = async (partId: number) => {
    try {
      // Instead of removing from Supabase, just remove from local state
      setSavedParts(savedParts.filter(part => part.id !== partId));
      toast.success("Item removed from saved list");
      
      // Original Supabase code commented out
      /*
      const { error } = await supabase
        .from('saved_items')
        .delete()
        .eq('user_id', user!.id)
        .eq('part_id', partId.toString());
        
      if (error) throw error;
      */
    } catch (error) {
      console.error("Error removing saved item:", error);
      toast.error("Failed to remove item from saved list");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header
        viewMode={undefined}
        setViewMode={undefined}
        useBackButton={true}
      />
      
      <div className="container flex-1 py-8">
        <h1 className="text-2xl font-bold mb-6">Saved Items</h1>
        
        {loading ? (
          <div>Loading your saved items...</div>
        ) : savedParts.length === 0 ? (
          <div className="text-center py-12">
            <BookmarkX className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-xl font-medium mb-2">No saved items yet</h2>
            <p className="text-muted-foreground mb-6">When you find parts you like, save them for later</p>
            <Button onClick={() => navigate("/")}>Browse Parts</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedParts.map(part => (
              <div key={part.id}>
                <PartCard 
                  part={part}
                  sortBy="none"
                  onRatingClick={() => {}}
                  activeFilters={[]}
                  onHeartClick={handleRemoveFromSaved}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedItems;
